import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { actionPostLogin } from "../../features/Login/slices";
import { getUser, setUser } from "../../helpers/user";
import Button from "../../components/Button";
import Input from "../../components/Inputs";
import "./_styles.scss";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
// @ts-ignore: Allow importing image asset without type declaration
import IkoneLogo from "../../assets/ikone.png";
import { toast } from "sonner";
import {
  verificar2FA,
  verificarEAtivar2FA,
} from "../../features/2fa/validar-2fa/slices";
import { resetAtivar2FAState } from "../../features/2fa/slices";
import { Eye, EyeOff, LockKeyhole } from "lucide-react";
import * as versionData from "../../version.json";
import { InputChange } from "../../global/type";

interface IStatePageLogin {
  user: string;
  pass: string;
  code: string;
}

const PageLogin = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isTwoFactorStep, setIsTwoFactorStep] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useAppDispatch();
  const { major, minor, patch, build } = versionData || {
    major: 1,
    minor: 0,
    patch: 0,
    build: 0,
  };
  const versaoFormatada = `${major}.${minor}.${patch}.${build}`;
  const {
    status: loginStatus,
    user: loggedInUser,
    feedbackError,
  } = useAppSelector((state) => state.getLogin);

  const { status: twoFAStatus, userData: twoFAUserData } = useAppSelector(
    (state) => state.verificar2fa,
  );

  const user = getUser();

  useEffect(() => {
    const callbackValue = searchParams.get("callbackUrl");

    if (callbackValue) {
      const extraParams = new URLSearchParams(searchParams);
      extraParams.delete("callbackUrl");
      const queryStringExtras = extraParams.toString();

      const destinoFinal = queryStringExtras
        ? `${callbackValue}?${queryStringExtras}`
        : callbackValue;

      if (user) {
        console.log("Usuário já logado. Redirecionando para:", destinoFinal);
        navigate(`/${destinoFinal}`, { replace: true });
        return;
      }

      localStorage.setItem("callbackUrl", destinoFinal);

      setSearchParams(new URLSearchParams(), { replace: true });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [searchParams, setSearchParams, user, navigate]);

  const initialState: IStatePageLogin = {
    user: "",
    pass: "",
    code: "",
  };

  const [state, setState] = React.useState(initialState);

  const handlerLogin = async (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();

    if (!isTwoFactorStep) {
      if (state.user === "" || state.pass === "") {
        toast.error("Preencha o usuário e a senha.");
        return;
      }

      const resultAction = await dispatch(
        actionPostLogin({ user: state.user, pass: state.pass }),
      );

      if (actionPostLogin.fulfilled.match(resultAction)) {
        if (resultAction.payload.twoFactorRequired) {
          setIsTwoFactorStep(true);
        }
      }
    } else {
      if (state.code.length !== 6) {
        toast.error("O código de verificação deve ter 6 dígitos.");
        return;
      }

      await dispatch(verificar2FA({ login: state.user, code: state.code }));
    }
  };

  const handlerChange = (ev: InputChange) => {
    const { name, value } = ev.currentTarget;
    setState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  React.useEffect(() => {
    if (twoFAStatus === "success" && twoFAUserData) {
      setUser(twoFAUserData)
        .then(() => {
          const redirectUrl = localStorage.getItem("callbackUrl");
          localStorage.removeItem("callbackUrl");
          navigate(redirectUrl || "/dashboard", { replace: true });
        })
        .catch(() => navigate("/"));
    }

    if (twoFAStatus === "error") {
      toast.error("Código inválido. Tente novamente.");
    }
  }, [twoFAStatus, twoFAUserData, navigate]);

  React.useEffect(() => {
    if (loginStatus === "") return;

    if (loginStatus === "failed") {
      toast.error("Falha: houve um erro ao entrar");
    }

    if (loginStatus === "success") {
      if (isTwoFactorStep) return;

      setUser(loggedInUser)
        .then(() => {
          const redirectUrl = localStorage.getItem("callbackUrl");
          localStorage.removeItem("callbackUrl");

          console.log("Usuário logado com sucesso:", loggedInUser);
          navigate(redirectUrl || "/dashboard", { replace: true });
        })
        .catch(() => navigate("/"));
    }
  }, [loginStatus, loggedInUser, navigate, isTwoFactorStep]);

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const isLoading = loginStatus === "loading";

  console.log("isTwoFactorStep:", isTwoFactorStep);
  console.log("loginStatus:", loginStatus);

  return (
    <div className="page-login">
      <form className="form-login" onSubmit={handlerLogin}>
        <h1 className="text-center heading--md mb-10">
          {!isTwoFactorStep ? "Login" : "Verificação de Dois Fatores"}
        </h1>

        {!isTwoFactorStep ? (
          <div className="form-login__inputs">
            <Input
              type="text"
              name="user"
              label="Usuário"
              size="sm"
              value={state.user}
              onChange={handlerChange}
              disabled={isLoading}
            />
            <Input
              type={showPassword ? "text" : "password"}
              name="pass"
              label="Senha"
              size="sm"
              value={state.pass}
              onChange={handlerChange}
              disabled={isLoading}
              endAdornment={
                <button
                  type="button"
                  className="btn-toggle-password"
                  onClick={() => setShowPassword((prev) => !prev)}
                  tabIndex={-1}
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              }
            />
          </div>
        ) : (
          <>
            <div className="card-header">
              <div className="icon-wrapper">
                <LockKeyhole size={32} color="#2563eb" />{" "}
              </div>
              <h2>Verificação de Segurança</h2>
              <p>
                Para sua segurança, insira o código de 6 dígitos do seu
                autenticador.
              </p>
            </div>

            <div className="form-login__inputs">
              <input
                type="text"
                name="code"
                placeholder="000 000"
                value={state.code}
                maxLength={6}
                onChange={handlerChange}
                disabled={isLoading}
                className="input-code-pin"
                autoFocus
              />
            </div>
          </>
        )}

        {!isTwoFactorStep && (
          <div className="mb-40 containerAtivarEsqueci">
            <a href="/Ativar-acesso">Ativar Acesso</a>
            <a href="/Forgot-pass">Esqueci a senha</a>
          </div>
        )}

        <Button
          size="sm"
          type="submit"
          variant="primary"
          loading={isLoading}
          disabled={isLoading}
        >
          {!isTwoFactorStep ? "Entrar" : "Verificar"}
        </Button>
      </form>
      <div className="page-login__footer">
        <p>desenvolvido por</p>
        <img
          src={IkoneLogo}
          style={{ objectFit: "contain" }}
          alt="Logotipo da Ikone"
        />
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginTop: "10px",
        }}
      >
        <small style={{ color: "#aaa7a7" }}>Versão: {versaoFormatada}</small>
      </div>
    </div>
  );
};
export default PageLogin;
